import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Camera, Plus, MapPin, Instagram, Youtube, Loader2 } from "lucide-react";
import OnboardingShell from "@/components/jk/OnboardingShell";
import { PrimaryButton, TextField, TextArea } from "@/components/jk/ui";
import { Chip, SectionLabel } from "@/components/jk/kit";
import { useAuth } from "@/context/AuthContext";
import { useMenu } from "@/context/MenuContext";
import api from "@/lib/api";

const SPECIALISATIONS = ["KP Astrology", "Nadi Astrology", "Tamil Astrology", "Vedic Astrology", "Numerology", "Vastu", "Palmistry", "Tarot", "Gemmology"];
const LANGUAGES = ["Tamil", "English", "Hindi", "Telugu", "Kannada", "Malayalam"];

const OnboardingProfile = () => {
  const navigate = useNavigate();
  const { astrologer, setAstrologer, onboardingComplete } = useAuth();
  const { open: openMenu } = useMenu();
  const fileRef = useRef(null);
  const editMode = !!onboardingComplete;
  const backToMenu = () => { openMenu(); navigate(-1); };

  const [photo, setPhoto] = useState("");
  const [fullName, setFullName] = useState("");
  const [city, setCity] = useState("");
  const [specs, setSpecs] = useState([]);
  const [langs, setLangs] = useState([]);
  const [years, setYears] = useState("");
  const [about, setAbout] = useState("");
  const [instagram, setInstagram] = useState("");
  const [youtube, setYoutube] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  useEffect(() => {
    if (astrologer) {
      setPhoto(astrologer.photo_url || "");
      setFullName(astrologer.full_name || "");
      setCity(astrologer.city || "");
      setSpecs(astrologer.specialisations || []);
      setLangs(astrologer.languages || []);
      setYears(astrologer.years_experience ? String(astrologer.years_experience) : "");
      setAbout(astrologer.about || "");
      setInstagram(astrologer.instagram_url || "");
      setYoutube(astrologer.youtube_url || "");
    }
  }, [astrologer]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError("");
    setPhotoUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/astrologer/upload-photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhoto(data.photo_url);
    } catch {
      setPhotoError("Photo upload failed. Please try again.");
    } finally {
      setPhotoUploading(false);
    }
  };

  const toggle = (list, setList, item) => {
    const set = new Set(list);
    set.has(item) ? set.delete(item) : set.add(item);
    setList([...set]);
  };

  const handleContinue = async () => {
    if (!fullName.trim()) { setError("Please enter your full name."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/astrologer/profile", {
        full_name: fullName.trim(), city: city.trim(), photo_url: photo,
        specialisations: specs, languages: langs,
        years_experience: parseInt(years || "0", 10) || 0, about: about.trim(),
        instagram_url: instagram.trim(), youtube_url: youtube.trim(),
      });
      setAstrologer(data.astrologer);
      if (editMode) { toast.success("Profile saved"); backToMenu(); }
      else navigate("/onboarding/services");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingShell
      step={editMode ? null : 1}
      title={editMode ? "Edit Profile" : "Your Profile"}
      subtitle="Set up your profile so clients can find and book you."
      onBack={() => editMode ? backToMenu() : navigate("/signin")}
      testid="onboarding-profile-screen"
      footer={
        <PrimaryButton testid="profile-continue" onClick={handleContinue} disabled={saving} icon={<ArrowRight size={18} strokeWidth={2} />}>
          {saving ? "Saving…" : editMode ? "Save changes" : "Continue"}
        </PrimaryButton>
      }
    >
      {/* Photo */}
      <div className="mb-7 flex justify-center">
        <button type="button" onClick={() => !photoUploading && fileRef.current?.click()} className="relative" data-testid="photo-upload" disabled={photoUploading}>
          {photoUploading ? (
            <div className="flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 border-[#e5e5e5] bg-white">
              <Loader2 size={22} strokeWidth={1.5} className="animate-spin text-[#aaaaaa]" />
            </div>
          ) : photo ? (
            <img src={photo} alt="Profile" className="h-24 w-24 rounded-full border border-[#e5e5e5] object-cover" />
          ) : (
            <div className="flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 border-dashed border-[#e5e5e5] bg-white">
              <Camera size={22} strokeWidth={1.5} className="text-[#aaaaaa]" />
              <span className="mt-1 text-[11px] text-[#666666]">Add your photo</span>
            </div>
          )}
          <span className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full bg-[#111111]">
            <Plus size={16} strokeWidth={2.5} className="text-white" />
          </span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} />
        {photoError && <p className="mt-2 text-center text-[11px] text-red-500" data-testid="photo-error">{photoError}</p>}
        <p className="mt-3 text-center text-[11px] leading-snug text-[#aaaaaa]">Best size: 440 x 360px (landscape).<br />Your photo will be cropped to fill this area — keep your face centred.</p>
      </div>

      <div className="space-y-5">
        <TextField testid="full-name-input" label="Full Name" placeholder="Enter your full name" value={fullName}
          onChange={(e) => { setFullName(e.target.value); setError(""); }} error={error} />
        <TextField testid="city-input" label="City" placeholder="Where are you based?" value={city}
          onChange={(e) => setCity(e.target.value)} rightIcon={<MapPin size={18} strokeWidth={1.5} />} />

        <div>
          <SectionLabel className="mb-3">Specialisation</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            {SPECIALISATIONS.map((s) => (
              <Chip key={s} full selected={specs.includes(s)} onClick={() => toggle(specs, setSpecs, s)} testid={`spec-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</Chip>
            ))}
          </div>
        </div>

        <div>
          <SectionLabel className="mb-3">Languages</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((l) => (
              <Chip key={l} selected={langs.includes(l)} onClick={() => toggle(langs, setLangs, l)} testid={`lang-${l.toLowerCase()}`}>{l}</Chip>
            ))}
          </div>
        </div>

        <TextField testid="years-input" label="Years of Experience" type="number" inputMode="numeric" placeholder="0" value={years}
          onChange={(e) => setYears(e.target.value)} />
        <TextArea testid="about-input" label="About" rows={3} placeholder="Tell clients about your background and expertise…" value={about}
          maxLength={300} onChange={(e) => setAbout(e.target.value)} />
        <p className="mt-1 text-right text-[11px] text-[#aaaaaa]">{about.length} / 300 characters</p>

        <div>
          <SectionLabel className="mb-3">Social links <span className="font-normal text-[#999]">(optional)</span></SectionLabel>
          <div className="space-y-4">
            <TextField testid="instagram-input" label="Instagram" type="url" inputMode="url" placeholder="https://instagram.com/yourhandle" value={instagram}
              onChange={(e) => setInstagram(e.target.value)} rightIcon={<Instagram size={18} strokeWidth={1.5} />} />
            <TextField testid="youtube-input" label="YouTube" type="url" inputMode="url" placeholder="https://youtube.com/@yourchannel" value={youtube}
              onChange={(e) => setYoutube(e.target.value)} rightIcon={<Youtube size={18} strokeWidth={1.5} />} />
          </div>
        </div>
      </div>
    </OnboardingShell>
  );
};

export default OnboardingProfile;
